import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { Download, ShieldCheck, Warning, ArrowSquareOut, Trash, Info } from "@phosphor-icons/react";

/**
 * Coachee-only "Mijn privacy" page.
 * - Toon link naar de publieke privacyverklaring.
 * - Knop om alle eigen gegevens te downloaden (Art. 15 / 20).
 * - Verzoek tot verwijdering indienen / status bekijken / annuleren (Art. 17).
 */
export default function MyPrivacyPage() {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showDeletionForm, setShowDeletionForm] = useState(false);
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/me/deletion-request");
      setRequest(data.request || null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDownload = useCallback(async () => {
    setError("");
    setDownloading(true);
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `mijn-gegevens-neuro-job-coach-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setInfo("Je gegevens zijn gedownload als JSON-bestand.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setDownloading(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setInfo("");
    try {
      const { data } = await api.post("/me/deletion-request", {
        password,
        reason: reason.trim() || undefined,
      });
      setRequest(data.request);
      setShowDeletionForm(false);
      setPassword("");
      setReason("");
      setInfo("Je verzoek tot verwijdering is geregistreerd. Je work coach beoordeelt het binnen 30 dagen.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Weet je zeker dat je je verzoek tot verwijdering wilt annuleren?")) return;
    setCancelling(true);
    setError("");
    try {
      await api.delete("/me/deletion-request");
      setRequest(null);
      setInfo("Je verzoek is geannuleerd. Je account blijft actief.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <AppLayout
      title="Mijn privacy"
      breadcrumbs={[{ label: "Tests", to: "/tests" }, { label: "Mijn privacy" }]}
    >
      <div className="max-w-3xl space-y-6" data-testid="my-privacy-page">
        <div className="neuro-card-bold p-6 bg-white">
          <div className="flex items-start gap-3">
            <ShieldCheck size={28} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Jouw rechten onder de AVG</h2>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Hier kun je je rechten als betrokkene uitoefenen: je gegevens inzien, downloaden of laten verwijderen.
                Voor uitgebreide uitleg kun je de{" "}
                <Link to="/privacy" className="underline font-medium" data-testid="my-privacy-link-statement">
                  privacyverklaring <ArrowSquareOut size={12} className="inline" />
                </Link>{" "}
                lezen.
              </p>
            </div>
          </div>
        </div>

        {info && (
          <div className="border border-[#43A047]/30 bg-[#43A047]/5 text-[#43A047] text-sm px-4 py-3 rounded-sm" data-testid="my-privacy-info">{info}</div>
        )}
        {error && (
          <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="my-privacy-error">{error}</div>
        )}

        <div className="neuro-card p-6 bg-white">
          <div className="flex items-start gap-3">
            <Download size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-display text-lg font-bold tracking-tight">Download al mijn gegevens</h3>
              <p className="text-sm text-zinc-600 mt-1">
                Krijg een volledige kopie van alle persoonsgegevens die wij van jou bewaren: CV, testresultaten, profielen en AI-gespreksverlopen. Het bestand is een gestructureerd JSON-bestand.
              </p>
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                data-testid="my-privacy-download-btn"
                className="mt-3 inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
              >
                <Download size={16} /> {downloading ? "Bezig…" : "Download (JSON)"}
              </button>
            </div>
          </div>
        </div>

        <div className="neuro-card p-6 bg-white">
          <div className="flex items-start gap-3">
            <Trash size={26} weight="duotone" className="text-[#E53935] shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-display text-lg font-bold tracking-tight">Verzoek tot verwijdering</h3>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                Onder de AVG (artikel 17 — recht op vergetelheid) kun je vragen om al je gegevens permanent te laten wissen.
                Je verzoek wordt door je work coach of een administrator binnen <strong>30 dagen</strong> beoordeeld.
                Na goedkeuring worden je account én alle bijbehorende gegevens onomkeerbaar verwijderd.
              </p>

              {loading ? (
                <div className="text-sm text-zinc-500 mt-3">Laden…</div>
              ) : request ? (
                <div className="mt-4 border border-amber-300 bg-amber-50 rounded-sm p-4" data-testid="my-privacy-pending-request">
                  <div className="flex items-start gap-2">
                    <Info size={20} className="text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-sm text-zinc-800 leading-relaxed">
                      <div className="font-semibold text-amber-800">Je verzoek staat open ({request.status === "pending" ? "in behandeling" : request.status}).</div>
                      <ul className="mt-2 space-y-1">
                        <li>Ingediend op: {new Date(request.requested_at).toLocaleString()}</li>
                        <li>Uiterlijk afgehandeld vóór: {new Date(request.deadline).toLocaleDateString()}</li>
                        {request.reason && <li>Reden: <em>&ldquo;{request.reason}&rdquo;</em></li>}
                      </ul>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={cancelling}
                    data-testid="my-privacy-cancel-deletion-btn"
                    className="mt-3 inline-flex items-center gap-2 border-2 border-zinc-300 text-zinc-800 px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-zinc-100 transition-colors disabled:opacity-50"
                  >
                    {cancelling ? "Annuleren…" : "Verzoek intrekken"}
                  </button>
                </div>
              ) : !showDeletionForm ? (
                <button
                  type="button"
                  onClick={() => { setShowDeletionForm(true); setError(""); setInfo(""); }}
                  data-testid="my-privacy-request-deletion-btn"
                  className="mt-3 inline-flex items-center gap-2 border-2 border-[#E53935] text-[#E53935] px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#E53935] hover:text-white transition-colors"
                >
                  <Trash size={16} /> Verzoek indienen
                </button>
              ) : (
                <form onSubmit={handleSubmit} className="mt-4 space-y-3 border-2 border-[#E53935]/40 bg-[#E53935]/5 rounded-sm p-4" data-testid="my-privacy-deletion-form">
                  <div className="flex items-start gap-2 text-sm text-[#C62828]">
                    <Warning size={18} className="shrink-0 mt-0.5" />
                    <div>
                      Bevestig met je wachtwoord. Na goedkeuring is je account en alle data <strong>definitief verloren</strong> — wij kunnen niets terugzetten.
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1">Huidig wachtwoord</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="my-privacy-deletion-password"
                      className="w-full px-3 py-2 border-2 border-zinc-300 bg-white rounded-sm text-sm focus:outline-none focus:border-zinc-900"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1">Reden (optioneel)</label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      maxLength={1000}
                      data-testid="my-privacy-deletion-reason"
                      className="w-full px-3 py-2 border-2 border-zinc-300 bg-white rounded-sm text-sm resize-none focus:outline-none focus:border-zinc-900"
                    />
                  </div>
                  <div className="flex gap-2 justify-end pt-1">
                    <button
                      type="button"
                      onClick={() => { setShowDeletionForm(false); setPassword(""); setReason(""); }}
                      disabled={submitting}
                      data-testid="my-privacy-deletion-cancel"
                      className="px-4 py-2 text-sm font-medium border border-zinc-300 rounded-sm hover:bg-zinc-100 transition-colors"
                    >
                      Annuleren
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !password}
                      data-testid="my-privacy-deletion-submit"
                      className="px-4 py-2 text-sm font-semibold uppercase tracking-wide bg-[#E53935] text-white rounded-sm hover:bg-[#C62828] transition-colors disabled:bg-zinc-400"
                    >
                      {submitting ? "Indienen…" : "Verzoek indienen"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
